import { Typography } from '@mui/material';
import MUIContainer from '@mui/material/Container';
import { DateTime } from 'luxon';
import React, { type ForwardedRef, forwardRef } from 'react';

import {
  Cell,
  CellContent,
  Circle,
  Container,
  Header,
  HeaderLabel,
  HeaderRow,
  HeaderText,
  HeaderTextContainer,
  Logo,
  PatientDetails,
  Row,
  Table,
} from './elements.js';
import { type CheckinDailyReport, type CheckInReportDTO } from '../../api/index.js';
import logoSmall from '../../assets/logo-stacked-smaller.svg';
import { hideWord } from '../../utils/index.js';



interface PatientCheckInReportProps {
  data: CheckInReportDTO;
  showPatientName: boolean;
}

export const PatientCheckInReport = forwardRef(
  (props: PatientCheckInReportProps, ref: ForwardedRef<HTMLDivElement>) => {
    const name = props.showPatientName ? props.data.patientName : hideWord(props.data.patientName);
    const patientId = props.showPatientName ? props.data.patientId : hideWord(props.data.patientId, '-');
    const timezone = props.data.timezone;
    const groupByDay = (data: CheckInReportDTO): CheckinDailyReport[][] => {
      const result: CheckinDailyReport[][] = [];
      let groupedData: CheckinDailyReport[] = [];
      for (const row of data.report.data) {
        groupedData.push(row);
        if (groupedData.length === data.report.header.length) {
          result.push(groupedData);
          groupedData = [];
        }
      }
      return result;
    };

    return (
      <MUIContainer ref={ref} maxWidth={'lg'}>
        <Container key={props.data.patientId}>
          <Header>
            <Logo>
              <img src={logoSmall} alt="Onekeycare Logo" />
            </Logo>
            <PatientDetails>
              <HeaderTextContainer>
                <HeaderLabel>Name:</HeaderLabel>
                <HeaderText>{name}</HeaderText>
              </HeaderTextContainer>
              <HeaderTextContainer>
                <HeaderLabel>Client ID:</HeaderLabel>
                <HeaderText>{patientId}</HeaderText>
              </HeaderTextContainer>
            </PatientDetails>
          </Header>
          <Typography variant={'h6'} sx={{ mb: 2, color: '#2f3659', textAlign: 'center', fontWeight: 'bold' }}>
            Check-in Report
          </Typography>
          <Table>
            <HeaderRow>
              <Cell header>Date</Cell>
              {props.data.report.header.map((header) => (
                <Cell key={header} header>
                  {header}
                </Cell>
              ))}
            </HeaderRow>
            {groupByDay(props.data).map((row, index) => (
              <Row key={index}>
                <Cell data-title="Date">
                  {DateTime.fromISO(row[0].time).setZone(timezone).toFormat('D')}
                </Cell>
                {row.map((item, slotIndex) => (
                  <Cell key={slotIndex} data-title={props.data.report.header[slotIndex]}>
                    {item.isScheduled === false ? (
                      <CellContent>—</CellContent>
                    ) : (
                      <CellContent>
                        <Circle color={item.color} small /> {item.name}
                      </CellContent>
                    )}
                  </Cell>
                ))}
              </Row>
            ))}
          </Table>
        </Container>
      </MUIContainer>
    );
  },
);
